import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import {
  Business as BusinessIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import DeliveryCenterCostsService from "../../../api/DeliveryCenter/deliveryCenterCosts";

const ACCENT = "#059669";
const ACCENT_DARK = "#047857";
const ACCENT_BG = "#064e3b";

const SupplierModal = ({ isOpen, onClose, editMode, initialData, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({ ...initialData });
    } else if (isOpen && !editMode) {
      setFormData({
        name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
      });
    }
    if (isOpen) {
      setErrors({});
    }
  }, [initialData, isOpen, editMode]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Nome do fornecedor e obrigatorio";

    if (
      formData.contact_email &&
      !/\S+@\S+\.\S+/.test(formData.contact_email)
    ) {
      newErrors.contact_email =
        "Por favor, insira um endereco de e-mail valido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      if (editMode) {
        await DeliveryCenterCostsService.updateSupplier(formData.id, formData);
      } else {
        await DeliveryCenterCostsService.createSupplier(formData);
      }

      onSuccess(
        editMode
          ? "Fornecedor atualizado com sucesso!"
          : "Fornecedor criado com sucesso!"
      );
      onClose();
    } catch (err) {
      console.error("Erro ao salvar fornecedor:", err);
      setErrors({
        submit: "Falha ao salvar. Por favor, tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&:hover fieldset": { borderColor: alpha(ACCENT, 0.4) },
      "&.Mui-focused fieldset": { borderColor: ACCENT },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.95, y: 10 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 10 },
        transition: { duration: 0.25 },
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${ACCENT_BG} 0%, ${ACCENT_DARK} 50%, ${ACCENT} 100%)`,
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha("#fff", 0.15),
              backdropFilter: "blur(8px)",
            }}
          >
            <BusinessIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                lineHeight: 1.3,
                color: "#ffffff",
              }}
            >
              {editMode ? "Editar Fornecedor" : "Novo Fornecedor"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: alpha("#fff", 0.55), fontSize: "0.68rem" }}
            >
              {editMode
                ? "Atualize os dados do fornecedor"
                : "Cadastre um novo parceiro comercial"}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 0,
            p: 0.75,
            color: alpha("#fff", 0.6),
            borderRadius: 1.5,
            "&:hover": {
              backgroundColor: alpha("#fff", 0.1),
              color: "#fff",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {errors.submit && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: 2 }}
            onClose={() =>
              setErrors((prev) => ({ ...prev, submit: undefined }))
            }
          >
            {errors.submit}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do Fornecedor"
              required
              value={formData.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
              variant="outlined"
              size="small"
              error={!!errors.name}
              helperText={errors.name}
              disabled={loading}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do Contato"
              value={formData.contact_name || ""}
              onChange={(e) => handleChange("contact_name", e.target.value)}
              variant="outlined"
              size="small"
              disabled={loading}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="E-mail do Contato"
              type="email"
              value={formData.contact_email || ""}
              onChange={(e) => handleChange("contact_email", e.target.value)}
              variant="outlined"
              size="small"
              error={!!errors.contact_email}
              helperText={errors.contact_email}
              disabled={loading}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Telefone do Contato"
              value={formData.contact_phone || ""}
              onChange={(e) => handleChange("contact_phone", e.target.value)}
              variant="outlined"
              size="small"
              disabled={loading}
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.grey[50], 0.5),
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: theme.palette.text.secondary,
            borderRadius: 2,
          }}
        >
          Cancelar
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          startIcon={<SaveIcon />}
          loadingPosition="start"
          disabled={!formData.name}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
            boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
            "&:hover": {
              boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
            },
          }}
        >
          {editMode ? "Atualizar" : "Criar"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierModal;
